/** タスクのレポート集計。 */

import {
  addDays,
  addMonths,
  startOfWeek,
  toDateKey,
  toMonthKey,
  toWeekKey,
  weekKeyToStartDate,
} from './datetime';
import type { TaskDef, TaskEntry, TaskValue } from '../domain/types';

export type ReportRange = 'week' | 'month' | 'half' | 'year';

export const RANGE_LABELS: Record<ReportRange, string> = {
  week: '週',
  month: '月',
  half: '半年',
  year: '年',
};

export interface Bucket {
  /** 表示ラベル。 */
  label: string;
  /** 期間の開始日（YYYY-MM-DD）。 */
  from: string;
  to: string;
  /** 集計値。記録がなければ null。 */
  value: number | null;
  /** この期間に記録があった回数。 */
  count: number;
  /** この期間に記録しうる回数（チェックの達成率の分母）。 */
  slots: number;
  /** テキストタスクの内容。 */
  texts: { date: string; text: string }[];
}

export interface ReportResult {
  title: string;
  buckets: Bucket[];
  /** 数値: 合計 / チェック: 達成回数 / テキスト: 記録件数 */
  total: number;
  average: number | null;
  max: number | null;
  min: number | null;
  /** 記録があった回数 */
  count: number;
  /** 記録しうる回数 */
  slots: number;
  /** 目標を満たした回数（target 指定時のみ） */
  hit: number | null;
  /** 現在の連続記録（チェックのみ、日次） */
  streak: number | null;
  bestStreak: number | null;
}

function numeric(v: TaskValue | undefined): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  return null;
}

/** 期間の範囲と、そのなかの区切りを決める。 */
export function buildRange(
  task: TaskDef,
  range: ReportRange,
  anchor: Date,
): { from: Date; to: Date; slices: { label: string; from: Date; to: Date }[]; title: string } {
  const dayLabel = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`;

  if (range === 'week') {
    const start = startOfWeek(anchor);
    const end = addDays(start, 6);
    return {
      from: start,
      to: end,
      title: `${start.getFullYear()}年${start.getMonth() + 1}月${start.getDate()}日 〜 ${end.getMonth() + 1}月${end.getDate()}日`,
      slices: Array.from({ length: 7 }, (_, i) => {
        const d = addDays(start, i);
        return { label: dayLabel(d), from: d, to: d };
      }),
    };
  }

  if (range === 'month') {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    const title = `${first.getFullYear()}年${first.getMonth() + 1}月`;
    if (task.kind === 'weekly') {
      // 週次タスクは週で区切る
      const slices: { label: string; from: Date; to: Date }[] = [];
      let w = startOfWeek(first);
      while (w <= last) {
        slices.push({ label: `${w.getMonth() + 1}/${w.getDate()}〜`, from: w, to: addDays(w, 6) });
        w = addDays(w, 7);
      }
      return { from: first, to: last, title, slices };
    }
    return {
      from: first,
      to: last,
      title,
      slices: Array.from({ length: last.getDate() }, (_, i) => {
        const d = new Date(first.getFullYear(), first.getMonth(), i + 1);
        return { label: String(i + 1), from: d, to: d };
      }),
    };
  }

  // 半年 / 年 は月で区切る（アンカー月を含む直近 n か月）
  const months = range === 'half' ? 6 : 12;
  const endMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const startMonth = addMonths(endMonth, -(months - 1));
  const slices = Array.from({ length: months }, (_, i) => {
    const m = addMonths(startMonth, i);
    return {
      label: `${m.getMonth() + 1}月`,
      from: m,
      to: new Date(m.getFullYear(), m.getMonth() + 1, 0),
    };
  });
  return {
    from: startMonth,
    to: new Date(endMonth.getFullYear(), endMonth.getMonth() + 1, 0),
    title: `${startMonth.getFullYear()}年${startMonth.getMonth() + 1}月 〜 ${endMonth.getFullYear()}年${endMonth.getMonth() + 1}月`,
    slices,
  };
}

/** その区間に「記録しうる回数」。タスク作成日より前は数えない。 */
function slotsIn(task: TaskDef, from: Date, to: Date, today: Date): number {
  const created = new Date(task.createdAt);
  const s = from < created ? created : from;
  const e = to > today ? today : to;
  if (s > e) return 0;
  const days = Math.floor((toMidnight(e).getTime() - toMidnight(s).getTime()) / 86400000) + 1;
  return task.kind === 'daily' ? days : Math.max(1, Math.ceil(days / 7));
}

function toMidnight(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function computeReport(
  task: TaskDef,
  entries: TaskEntry[],
  range: ReportRange,
  anchor: Date,
  today = new Date(),
): ReportResult {
  const { from, to, slices, title } = buildRange(task, range, anchor);

  /** 期間キー → その期間の値 */
  const byPeriod = new Map<string, TaskValue>();
  for (const e of entries) {
    const v = e.values[task.id];
    if (v !== undefined) byPeriod.set(e.period, v);
  }

  /** ある日付範囲に含まれる「記録」を集める。 */
  const collect = (a: Date, b: Date) => {
    const out: { key: string; date: string; value: TaskValue }[] = [];
    if (task.kind === 'daily') {
      for (let d = toMidnight(a); d <= b; d = addDays(d, 1)) {
        const k = toDateKey(d);
        const v = byPeriod.get(k);
        if (v !== undefined) out.push({ key: k, date: k, value: v });
      }
    } else {
      const seen = new Set<string>();
      for (let d = toMidnight(a); d <= b; d = addDays(d, 1)) {
        const k = toWeekKey(d);
        if (seen.has(k)) continue;
        seen.add(k);
        const v = byPeriod.get(k);
        if (v !== undefined) out.push({ key: k, date: toDateKey(weekKeyToStartDate(k)), value: v });
      }
    }
    return out;
  };

  const agg = task.aggregate ?? 'sum';
  const buckets: Bucket[] = slices.map((s) => {
    const recs = collect(s.from, s.to);
    const nums = recs.map((r) => numeric(r.value)).filter((n): n is number => n !== null);
    let value: number | null = null;
    if (nums.length > 0) {
      if (task.type === 'check') value = nums.filter((n) => n > 0).length;
      else if (agg === 'avg') value = nums.reduce((a, b) => a + b, 0) / nums.length;
      else if (agg === 'last') value = nums[nums.length - 1];
      else value = nums.reduce((a, b) => a + b, 0);
    }
    return {
      label: s.label,
      from: toDateKey(s.from),
      to: toDateKey(s.to),
      value,
      count: recs.length,
      slots: slotsIn(task, s.from, s.to, today),
      texts:
        task.type === 'text'
          ? recs.map((r) => ({ date: r.date, text: String(r.value) }))
          : [],
    };
  });

  const all = collect(from, to);
  const nums = all.map((r) => numeric(r.value)).filter((n): n is number => n !== null);
  const positives = nums.filter((n) => n > 0);

  let total = 0;
  if (task.type === 'check') total = positives.length;
  else if (task.type === 'text') total = all.length;
  else total = nums.reduce((a, b) => a + b, 0);

  const slots = buckets.reduce((n, b) => n + b.slots, 0);
  const hit =
    task.target != null && task.type === 'number'
      ? nums.filter((n) => n >= (task.target as number)).length
      : null;

  // 連続記録（チェックの日次のみ）
  let streak: number | null = null;
  let bestStreak: number | null = null;
  if (task.type === 'check' && task.kind === 'daily') {
    let best = 0;
    let run = 0;
    const created = toMidnight(new Date(task.createdAt));
    for (let d = created; d <= toMidnight(today); d = addDays(d, 1)) {
      const v = byPeriod.get(toDateKey(d));
      if (v === true || (typeof v === 'number' && v > 0)) {
        run++;
        best = Math.max(best, run);
      } else {
        run = 0;
      }
    }
    streak = run;
    bestStreak = best;
  }

  return {
    title,
    buckets,
    total,
    average: nums.length > 0 ? nums.reduce((a, b) => a + b, 0) / nums.length : null,
    max: nums.length > 0 ? Math.max(...nums) : null,
    min: nums.length > 0 ? Math.min(...nums) : null,
    count: all.length,
    slots,
    hit,
    streak,
    bestStreak,
  };
}

export function shiftAnchor(range: ReportRange, anchor: Date, delta: number): Date {
  if (range === 'week') return addDays(anchor, delta * 7);
  if (range === 'month') return addMonths(anchor, delta);
  return addMonths(anchor, delta * (range === 'half' ? 6 : 12));
}

export function anchorKey(range: ReportRange, anchor: Date): string {
  if (range === 'week') return toWeekKey(anchor);
  return toMonthKey(anchor);
}
